import { watch } from 'vue';
import axios from '@/axios';
import { appState } from '@/appState';
import { store } from '@/legacyStore';
import { logout, updateSession } from '@/vuex-actions';
import { authFlow, createAuthApi, PostLoginSteps } from '@/lib/auth';
import { setupSessionWatch } from '@/lib/auth/sessionWatch';
const LAST_LOGIN_EMAIL_KEY = 'bcc-client-lastLoginEmail';
const LAST_LOGIN_REMEMBER_ME_KEY = 'bcc-client-lastLoginRememberMe';
/**
 * Wires the V2 auth once at app start: the API client, and the glue (spec §12) that keeps the legacy Vuex clientSession correct,
 * because other parts of the app, and today's router guard, still read it.
 */
export function setupAuthV2() {
    authFlow.configure(createAuthApi(axios));
    watch(() => appState.sessionData, (session) => {
        if (session)
            store.commit(updateSession, toClientSession(session));
        else if (store.getters.isLoggedIn)
            store.commit(logout);
    }, 
    // sync: Vuex has the session before the awaiting screen navigates or reloads the page
    { flush: 'sync' });
    setupSessionWatch();
}
/**
 * The legacy Vuex clientSession, in the shape the old session call answered, built from the session.
 * A new object: Vuex mutations (e.g. setIsProfileUpToDate) must not change appState's session.
 */
export function toClientSession(session) {
    return {
        siteId: session.siteId,
        userId: session.userId,
        // the legacy code knows no null: 0 was its "no institution"
        institutionId: session.institutionId ?? 0,
        userRole: session.roles[0] ?? '',
        permissions: session.permissions.map((p) => ({ code: p.code, access: p.access })),
        // App.vue hides the menu while it is false
        isProfileUpToDate: !session.postLoginSteps.some((s) => s.step === PostLoginSteps.ProfileUpdate),
        // the session timers' window: the login cookie's own, from the server
        msLength: session.sessionTimeoutSeconds * 1000,
    };
}
/**
 * Keeps the last login's email and its "remember me" choice; the email key is the old login screen's, which BankUploadsScreen still reads.
 * Without rememberMe (a password reset) the "remember me" choice stays as it was.
 */
export function rememberLastLogin(email, rememberMe) {
    localStorage.removeItem('annualQuestionnaireIsOpen');
    localStorage.setItem(LAST_LOGIN_EMAIL_KEY, email);
    if (rememberMe !== undefined)
        localStorage.setItem(LAST_LOGIN_REMEMBER_ME_KEY, String(rememberMe));
}
export function lastLogin() {
    return {
        email: localStorage.getItem(LAST_LOGIN_EMAIL_KEY) ?? '',
        rememberMe: localStorage.getItem(LAST_LOGIN_REMEMBER_ME_KEY) === 'true',
    };
}
